/* eslint-disable max-len */
import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './WeaveTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { mainColors } from '../../helpers/colors';
import ExportData from '../ExportData/ExportData';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CAlert,
  CRow,
  CCol,
  CFormInput,
  CFormSelect,
  CCollapse
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, cilPencil, cilTrash, cilRecycle, cilChevronRight, cilChevronDoubleDown, cilFilter, cilX, cilSearch } from '@coreui/icons';
import { deleteWeaveRequestAction } from '../../store/weave/weaveActions';
import { setAlertAction } from '../../store/alert/alertActions';
import { useTranslation } from '../../hooks/useTranslation';
import { copyWeave, getWeaveDropdown } from '../../services/endpoints';
import CaglikReport from '../../pages/Reports/CaglikReport/CaglikReport';

const WeaveTable = (props) => {
  const {
    weaveList,
    weaveData,
    deleteWeaveRequest,
    query,
    onFilterChange,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    onRefresh,
    setAlert
  } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['expand','index','WeaveCode','WeaveTypeName','TotalWarpWeight','TotalWeftWeight','TotalWeight','actions'], 'WeaveTable');

  // Display total warp/weft grams in thousands for table and export views.
  const formatTotalGram = (weight) => {
    if (!weight || weight === 0) return '';
    const scaled = Number(weight) / 10000;
    const fixed = Number(scaled).toFixed(2);
    const parts = fixed.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimalPart = parts[1] ?? '00';
    return `${integerPart}.${decimalPart}`;
  };

  const getWeaveColorGroupKey = (color) => {
    const threadType = Number(color?.ThreadType) || 0;
    const yarnGuid = color?.PrdYarnGuid ?? color?.YarnGuid ?? color?.YarnId ?? '';
    const colorGuid = color?.PrdColorGuid ?? color?.ColorGuid ?? color?.ColorId ?? '';
    return `${threadType}|${String(yarnGuid)}|${String(colorGuid)}`;
  };

  // Weights are defined per yarn+color row. Same row can appear in multiple positions,
  // so we count that row's weight once by grouping on ThreadType+YarnGuid+ColorGuid.
  const getGroupedTotalWeights = (weaveColors) => {
    const colors = Array.isArray(weaveColors) ? weaveColors : [];
    const grouped = new Map();

    colors.forEach((color) => {
      const threadType = Number(color?.ThreadType);
      if (threadType !== 1 && threadType !== 2) return;

      const key = getWeaveColorGroupKey(color);
      const existing = grouped.get(key) || { threadType, warpWeight: 0, weftWeight: 0 };

      if (threadType === 2) {
        const warpWeight = Number(color?.WaftWeight) || 0;
        if (!existing.warpWeight && warpWeight) existing.warpWeight = warpWeight;
      }

      if (threadType === 1) {
        const weftWeight = Number(color?.WeftWeight) || 0;
        if (!existing.weftWeight && weftWeight) existing.weftWeight = weftWeight;
      }

      grouped.set(key, existing);
    });

    let totalWarpRaw = 0;
    let totalWeftRaw = 0;

    grouped.forEach((group) => {
      totalWarpRaw += Number(group.warpWeight) || 0;
      totalWeftRaw += Number(group.weftWeight) || 0;
    });

    return {
      totalWarpRaw,
      totalWeftRaw,
      totalWeightRaw: totalWarpRaw + totalWeftRaw
    };
  };

  const columns = [
    { id: 'expand', label: '', sortable: false, filterable: false },
    { id: 'index', label: '#', sortable: false, filterable: false },
    { id: 'WeaveCode', label: t('weaveCodeTip'), sortable: true, filterable: true },
    { id: 'WeaveTypeName', label: t('weaveType'), sortable: true, filterable: true },
    { id: 'TotalWarpWeight', label: t('totalWarpWeight'), sortable: true, filterable: false },
    { id: 'TotalWeftWeight', label: t('totalWeftWeight'), sortable: true, filterable: false },
    { id: 'TotalWeight', label: `${t('totalWarpWeight')} + ${t('totalWeftWeight')}`, sortable: true, filterable: false },
    { id: 'actions', label: t('actions'), sortable: false, filterable: false }
  ];

  // Backend-supported filter fields
  const filterFields = [
    { id: 'WeaveCode', label: t('weaveCodeTip') },
    { id: 'YarnGuid', label: t('yarn') },
    { id: 'WeftPrdYarnGuid', label: `${t('weft')} ${t('yarn')}` },
    { id: 'WarpPrdYarnGuid', label: `${t('warp')} ${t('yarn')}` },
    { id: 'MinTotalWeight', label: `${t('min')} ${t('totalWarpWeight')} + ${t('totalWeftWeight')}` },
    { id: 'MaxTotalWeight', label: `${t('max')} ${t('totalWarpWeight')} + ${t('totalWeftWeight')}` },
    { id: 'PrdWeaveTypeGuid', label: t('weaveType') },
    { id: 'PrdCompositionGuid', label: t('composition') },
    { id: 'MinWarpDensity', label: `${t('min')} ${t('warpDensity')}` },
    { id: 'MaxWarpDensity', label: `${t('max')} ${t('warpDensity')}` },
    { id: 'MinWeftDensity', label: `${t('min')} ${t('weftDensity')}` },
    { id: 'MaxWeftDensity', label: `${t('max')} ${t('weftDensity')}` },
    { id: 'ReedWidth', label: t('reedWidth') },
    { id: 'MinTotalThreads', label: `${t('min')} ${t('totalThreads')}` },
    { id: 'MaxTotalThreads', label: `${t('max')} ${t('totalThreads')}` },
    { id: 'Description', label: t('description') },
    { id: 'ReedNo', label: t('reedNo') },
    { id: 'Dgts', label: t('dgts') },
    { id: 'FabricWidth', label: t('grayFabricWidth') },
    { id: 'FabricWeight', label: t('grayFabricWeight') },
  ];

  const [tableData, setTableData] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [copyModal, setCopyModal] = useState({
    open: false,
    item: null,
    weaveCode: '',
    isSaving: false,
    error: ''
  });
  const [caglikPreviewModal, setCaglikPreviewModal] = useState({ open: false, weaveId: '', weaveCode: '' });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [dropdownData, setDropdownData] = useState({});
  const [filters, setFilters] = useState({
    WeaveGuid: '',
    YarnGuid: '',
    WeftPrdYarnGuid: '',
    WarpPrdYarnGuid: '',
    MinTotalWeightWhole: '',
    MinTotalWeightDecimal: '',
    MaxTotalWeightWhole: '',
    MaxTotalWeightDecimal: '',
    PrdWeaveTypeGuid: '',
    WeaveCode: '',
    PrdCompositionGuid: '',
    MinWarpDensityWhole: '',
    MinWarpDensityDecimal: '',
    MaxWarpDensityWhole: '',
    MaxWarpDensityDecimal: '',
    MinWeftDensityWhole: '',
    MinWeftDensityDecimal: '',
    MaxWeftDensityWhole: '',
    MaxWeftDensityDecimal: '',
    ReedWidth: '',
    MinTotalThreads: '',
    MaxTotalThreads: '',
    Description: '',
    ReedNo: '',
    Dgts: '',
    FabricWidth: '',
    FabricWeightWhole: '',
    FabricWeightDecimal: ''
  });

  useEffect(() => {
    if (weaveList && Array.isArray(weaveList)) {
      setTableData(weaveList);
    }
  }, [weaveList]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const sessionUserId = localStorage.getItem('sessionUserId');
        const { data } = await getWeaveDropdown({ SessionUserId: sessionUserId });
        setDropdownData(data || {});
      } catch {
        setDropdownData({});
      }
    };

    fetchDropdowns();
  }, []);

  useEffect(() => {
    const toWeightPartsBy100 = (weight) => {
      if (weight === null || weight === undefined || weight === '') {
        return { whole: '', decimal: '' };
      }

      const numeric = Number(weight);
      if (!Number.isFinite(numeric)) {
        return { whole: '', decimal: '' };
      }

      const whole = Math.floor(Math.abs(numeric) / 100);
      const decimal = Math.abs(numeric) % 100;

      return {
        whole: String(whole),
        decimal: String(decimal).padStart(2, '0')
      };
    };

    const toWeightPartsBy10000 = (weight) => {
      if (weight === null || weight === undefined || weight === '') {
        return { whole: '', decimal: '' };
      }

      const numeric = Number(weight);
      if (!Number.isFinite(numeric)) {
        return { whole: '', decimal: '' };
      }

      const scaled = Math.abs(numeric) / 100;
      const whole = Math.floor(scaled / 100);
      const decimal = Math.floor(scaled % 100);

      return {
        whole: String(whole),
        decimal: String(decimal).padStart(2, '0')
      };
    };

    const weightParts = toWeightPartsBy100(query?.FabricWeight);
    const minTotalWeightParts = toWeightPartsBy10000(query?.MinTotalWeight);
    const maxTotalWeightParts = toWeightPartsBy10000(query?.MaxTotalWeight);
    const minWarpDensityParts = toDensityPartsBy100(query?.MinWarpDensity);
    const maxWarpDensityParts = toDensityPartsBy100(query?.MaxWarpDensity);
    const minWeftDensityParts = toDensityPartsBy100(query?.MinWeftDensity);
    const maxWeftDensityParts = toDensityPartsBy100(query?.MaxWeftDensity);

    const activeElementName = document?.activeElement?.getAttribute?.('name');
    const isWeightInputFocused = activeElementName === 'FilterFabricWeightWhole' || activeElementName === 'FilterFabricWeightDecimal';
    const isMinTotalWeightFocused = activeElementName === 'FilterMinTotalWeightWhole' || activeElementName === 'FilterMinTotalWeightDecimal';
    const isMaxTotalWeightFocused = activeElementName === 'FilterMaxTotalWeightWhole' || activeElementName === 'FilterMaxTotalWeightDecimal';
    const isMinWarpDensityFocused = activeElementName === 'FilterMinWarpDensityWhole' || activeElementName === 'FilterMinWarpDensityDecimal';
    const isMaxWarpDensityFocused = activeElementName === 'FilterMaxWarpDensityWhole' || activeElementName === 'FilterMaxWarpDensityDecimal';
    const isMinWeftDensityFocused = activeElementName === 'FilterMinWeftDensityWhole' || activeElementName === 'FilterMinWeftDensityDecimal';
    const isMaxWeftDensityFocused = activeElementName === 'FilterMaxWeftDensityWhole' || activeElementName === 'FilterMaxWeftDensityDecimal';

    setFilters((prev) => ({
      WeaveGuid: query?.WeaveGuid != null ? String(query.WeaveGuid) : '',
      YarnGuid: query?.YarnGuid != null ? String(query.YarnGuid) : '',
      WeftPrdYarnGuid: query?.WeftPrdYarnGuid != null ? String(query.WeftPrdYarnGuid) : '',
      WarpPrdYarnGuid: query?.WarpPrdYarnGuid != null ? String(query.WarpPrdYarnGuid) : '',
      MinTotalWeightWhole: isMinTotalWeightFocused ? prev.MinTotalWeightWhole : minTotalWeightParts.whole,
      MinTotalWeightDecimal: isMinTotalWeightFocused ? prev.MinTotalWeightDecimal : minTotalWeightParts.decimal,
      MaxTotalWeightWhole: isMaxTotalWeightFocused ? prev.MaxTotalWeightWhole : maxTotalWeightParts.whole,
      MaxTotalWeightDecimal: isMaxTotalWeightFocused ? prev.MaxTotalWeightDecimal : maxTotalWeightParts.decimal,
      PrdWeaveTypeGuid: query?.PrdWeaveTypeGuid != null ? String(query.PrdWeaveTypeGuid) : '',
      WeaveCode: query?.WeaveCode || '',
      PrdCompositionGuid: query?.PrdCompositionGuid != null ? String(query.PrdCompositionGuid) : '',
      MinWarpDensityWhole: isMinWarpDensityFocused ? prev.MinWarpDensityWhole : minWarpDensityParts.whole,
      MinWarpDensityDecimal: isMinWarpDensityFocused ? prev.MinWarpDensityDecimal : minWarpDensityParts.decimal,
      MaxWarpDensityWhole: isMaxWarpDensityFocused ? prev.MaxWarpDensityWhole : maxWarpDensityParts.whole,
      MaxWarpDensityDecimal: isMaxWarpDensityFocused ? prev.MaxWarpDensityDecimal : maxWarpDensityParts.decimal,
      MinWeftDensityWhole: isMinWeftDensityFocused ? prev.MinWeftDensityWhole : minWeftDensityParts.whole,
      MinWeftDensityDecimal: isMinWeftDensityFocused ? prev.MinWeftDensityDecimal : minWeftDensityParts.decimal,
      MaxWeftDensityWhole: isMaxWeftDensityFocused ? prev.MaxWeftDensityWhole : maxWeftDensityParts.whole,
      MaxWeftDensityDecimal: isMaxWeftDensityFocused ? prev.MaxWeftDensityDecimal : maxWeftDensityParts.decimal,
      ReedWidth: query?.ReedWidth != null ? String(query.ReedWidth) : '',
      MinTotalThreads: query?.MinTotalThreads != null ? String(query.MinTotalThreads) : '',
      MaxTotalThreads: query?.MaxTotalThreads != null ? String(query.MaxTotalThreads) : '',
      Description: query?.Description || '',
      ReedNo: query?.ReedNo != null ? String(query.ReedNo) : '',
      Dgts: query?.Dgts != null ? String(query.Dgts) : '',
      FabricWidth: query?.FabricWidth != null ? String(query.FabricWidth) : '',
      FabricWeightWhole: isWeightInputFocused ? prev.FabricWeightWhole : weightParts.whole,
      FabricWeightDecimal: isWeightInputFocused ? prev.FabricWeightDecimal : weightParts.decimal
    }));
  }, [query]);

  const toIntPrice = (whole, decimal) => {
    const wholeValue = String(whole || '').replace(/\D/g, '');
    const decimalValue = String(decimal || '').replace(/\D/g, '').slice(0, 2);

    if (!wholeValue && !decimalValue) {
      return null;
    }

    const wholePart = wholeValue ? parseInt(wholeValue, 10) : 0;
    const decimalPart = decimalValue ? parseInt(decimalValue.padEnd(2, '0'), 10) : 0;
    return (wholePart * 100) + decimalPart;
  };

  const toDensityPartsBy100 = (value) => {
    if (value === null || value === undefined || value === '') {
      return { whole: '', decimal: '' };
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return { whole: '', decimal: '' };
    }

    const formatted = (numeric / 100).toFixed(2);
    const [whole, decimal] = formatted.split('.');
    return { whole, decimal: decimal || '00' };
  };

  const toIntDensity = (whole, decimal) => {
    const wholeValue = String(whole || '').replace(/\D/g, '');
    const decimalValue = String(decimal || '').replace(/\D/g, '').slice(0, 2);

    if (!wholeValue && !decimalValue) {
      return null;
    }

    const wholePart = wholeValue ? parseInt(wholeValue, 10) : 0;
    const decimalPart = decimalValue ? parseInt(decimalValue.padEnd(2, '0'), 10) : 0;
    const scaledValue = (wholePart * 100) + decimalPart;

    return scaledValue === 0 ? null : scaledValue;
  };

  const formatDensity = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return (numeric / 100).toFixed(2).replace(/(\.\d)0$/, '$1');
  };

  const toIntFilterWeight = (whole, decimal) => {
    const wholeValue = String(whole || '').replace(/\D/g, '');
    const decimalValue = String(decimal || '').replace(/\D/g, '').slice(0, 2);

    if (!wholeValue && !decimalValue) {
      return null;
    }

    const wholePart = wholeValue ? parseInt(wholeValue, 10) : 0;
    const decimalPart = decimalValue ? parseInt(decimalValue.padEnd(2, '0'), 10) : 0;
    return (wholePart * 10000) + (decimalPart * 100);
  };

  const toDecimalWeight = (whole, decimal) => {
    const wholeValue = String(whole || '').replace(/\D/g, '');
    const decimalValue = String(decimal || '').replace(/\D/g, '').slice(0, 2);

    if (!wholeValue && !decimalValue) {
      return null;
    }

    const wholePart = wholeValue ? parseInt(wholeValue, 10) : 0;
    const decimalPart = decimalValue ? parseInt(decimalValue.padEnd(2, '0'), 10) : 0;
    return Number(`${wholePart}.${String(decimalPart).padStart(2, '0')}`);
  };

  const handleDecimalInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    const nextFilters = { ...filters, [field]: value };
    setFilters(nextFilters);
    triggerFilter(nextFilters);
  };

  const handleWholeInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '');
    const nextFilters = { ...filters, [field]: value };
    setFilters(nextFilters);
    triggerFilter(nextFilters);
  };

  const handlePriceKeyDown = (e, decimalField) => {
    const isDotOrComma = e.key === '.' || e.key === ',' ||
      e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
    if (isDotOrComma) {
      e.preventDefault();
      const decimalInput = document.querySelector(`input[name="${decimalField}"]`);
      if (decimalInput) {
        decimalInput.focus();
        decimalInput.select();
      }
    }
  };

  const handleEdit = (item) => {
    console.log('Editing weave:', item);
    navigate(`/weave/edit/${item.WeaveId}`, {
      state: { weave: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          WeaveId: deleteModal.item.WeaveId
        };

        console.log('Deleting weave:', deleteData);
        deleteWeaveRequest({ data: deleteData });
      } else {
        console.error('User session not found');
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleCopyClick = (item) => {
    setCopyModal({
      open: true,
      item,
      weaveCode: '',
      isSaving: false,
      error: ''
    });
  };

  const handleCopyCancel = () => {
    if (copyModal.isSaving) return;
    setCopyModal({ open: false, item: null, weaveCode: '', isSaving: false, error: '' });
  };

  const handleCopyCodeChange = (e) => {
    setCopyModal((prev) => ({
      ...prev,
      weaveCode: e.target.value,
      error: ''
    }));
  };

  const handleCopyConfirm = async () => {
    const weaveCode = copyModal.weaveCode.trim();
    const weaveId = copyModal.item?.WeaveId;

    if (!weaveCode) {
      setCopyModal((prev) => ({ ...prev, error: t('copyWeaveCodeRequired') }));
      return;
    }

    if (!weaveId) {
      setCopyModal((prev) => ({ ...prev, error: t('copyWeaveNotFound') }));
      return;
    }

    setCopyModal((prev) => ({ ...prev, isSaving: true, error: '' }));

    try {
      const sessionUserId = localStorage.getItem('sessionUserId');
      const { data } = await copyWeave({
        SessionUserId: sessionUserId,
        WeaveId: String(weaveId),
        WeaveCode: weaveCode
      });

      if (data?.Result?.ErrorCode === 0) {
        setAlert?.({
          title: 'Success',
          text: data.Result.ErrorDescription || 'Weave copied successfully.',
          variant: 'success'
        });
        setCopyModal({ open: false, item: null, weaveCode: '', isSaving: false, error: '' });
        onRefresh?.();
        return;
      }

      setCopyModal((prev) => ({
        ...prev,
        isSaving: false,
        error: data?.Result?.ErrorDescription || t('copyWeaveError')
      }));
    } catch (error) {
      setCopyModal((prev) => ({
        ...prev,
        isSaving: false,
        error: error?.response?.data?.Result?.ErrorDescription || t('copyWeaveError')
      }));
    }
  };

  const handleOpenCaglikPreview = (item) => {
    const weaveId = item?.WeaveId != null ? String(item.WeaveId) : '';
    if (!weaveId) return;

    setCaglikPreviewModal({
      open: true,
      weaveId,
      weaveCode: item?.WeaveCode || ''
    });
  };

  const handleCloseCaglikPreview = () => {
    setCaglikPreviewModal({ open: false, weaveId: '', weaveCode: '' });
  };

  const toggleRowExpansion = (weaveId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(weaveId)) {
      newExpandedRows.delete(weaveId);
    } else {
      newExpandedRows.add(weaveId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleChangePage = (newPage) => {
    onPageChange?.(newPage);
    scrollToTableContainer();
  };

  const handleChangeRowsPerPage = (value) => {
    onPageSizeChange?.(value);
    scrollToTableContainer();
  };

  const toNullableInt = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const toNullableString = (value) => {
    const raw = String(value ?? '').trim();
    return raw || null;
  };

  const toNullableDecimal = (value) => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const normalized = raw.replace(',', '.');
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const triggerFilter = (nextFilters) => {
    onFilterChange?.({
      WeaveGuid: toNullableString(nextFilters.WeaveGuid),
      YarnGuid: toNullableString(nextFilters.YarnGuid),
      WeftPrdYarnGuid: toNullableString(nextFilters.WeftPrdYarnGuid),
      WarpPrdYarnGuid: toNullableString(nextFilters.WarpPrdYarnGuid),
      MinTotalWeight: toIntFilterWeight(nextFilters.MinTotalWeightWhole, nextFilters.MinTotalWeightDecimal),
      MaxTotalWeight: toIntFilterWeight(nextFilters.MaxTotalWeightWhole, nextFilters.MaxTotalWeightDecimal),
      PrdWeaveTypeGuid: toNullableDecimal(nextFilters.PrdWeaveTypeGuid),
      WeaveCode: nextFilters.WeaveCode.trim() || null,
      PrdCompositionGuid: toNullableDecimal(nextFilters.PrdCompositionGuid),
      MinWarpDensity: toIntDensity(nextFilters.MinWarpDensityWhole, nextFilters.MinWarpDensityDecimal),
      MaxWarpDensity: toIntDensity(nextFilters.MaxWarpDensityWhole, nextFilters.MaxWarpDensityDecimal),
      WarpDensity: null,
      MinWeftDensity: toIntDensity(nextFilters.MinWeftDensityWhole, nextFilters.MinWeftDensityDecimal),
      MaxWeftDensity: toIntDensity(nextFilters.MaxWeftDensityWhole, nextFilters.MaxWeftDensityDecimal),
      WeftDensity: null,
      ReedWidth: toNullableInt(nextFilters.ReedWidth),
      MinTotalThreads: toNullableInt(nextFilters.MinTotalThreads),
      MaxTotalThreads: toNullableInt(nextFilters.MaxTotalThreads),
      TotalThreads: null,
      Description: nextFilters.Description.trim() || null,
      ReedNo: toNullableInt(nextFilters.ReedNo),
      Dgts: toNullableInt(nextFilters.Dgts),
      FabricWidth: toNullableInt(nextFilters.FabricWidth),
      FabricWeight: toIntPrice(nextFilters.FabricWeightWhole, nextFilters.FabricWeightDecimal)
    });
  };

  const handleRequestSort = (property) => {
    const orderByMap = {
      WeaveCode: 'weavecode',
      WeaveTypeName: 'prdweavetypeguid',
      TotalWarpWeight: 'totalwarpweight',
      TotalWeftWeight: 'totalweftweight',
      TotalWeight: 'totalweight'
    };

    const backendOrderBy = orderByMap[property];
    if (!backendOrderBy || !onSortChange) return;

    const currentOrderBy = query?.OrderBy || null;
    const currentDescending = query?.IsDescending !== false;
    const nextDescending = currentOrderBy === backendOrderBy ? !currentDescending : true;

    onSortChange(backendOrderBy, nextDescending);
  };

  const handleFilterChange = (key, value) => {
    const nextFilters = {
      ...filters,
      [key]: value
    };

    setFilters(nextFilters);
    triggerFilter(nextFilters);
  };

  const clearSingleFilter = (key) => {
    if (key === 'FabricWeight') {
      const nextFilters = {
        ...filters,
        FabricWeightWhole: '',
        FabricWeightDecimal: ''
      };

      setFilters(nextFilters);
      triggerFilter(nextFilters);
      return;
    }

    if (key === 'MinTotalWeight') {
      const nextFilters = {
        ...filters,
        MinTotalWeightWhole: '',
        MinTotalWeightDecimal: ''
      };

      setFilters(nextFilters);
      triggerFilter(nextFilters);
      return;
    }

    if (key === 'MaxTotalWeight') {
      const nextFilters = {
        ...filters,
        MaxTotalWeightWhole: '',
        MaxTotalWeightDecimal: ''
      };

      setFilters(nextFilters);
      triggerFilter(nextFilters);
      return;
    }

    if (['MinWarpDensity', 'MaxWarpDensity', 'MinWeftDensity', 'MaxWeftDensity'].includes(key)) {
      const nextFilters = {
        ...filters,
        [`${key}Whole`]: '',
        [`${key}Decimal`]: ''
      };

      setFilters(nextFilters);
      triggerFilter(nextFilters);
      return;
    }

    const nextFilters = {
      ...filters,
      [key]: ''
    };

    setFilters(nextFilters);
    triggerFilter(nextFilters);
  };

  const clearFilters = () => {
    const resetFilters = {
      WeaveGuid: '',
      YarnGuid: '',
      WeftPrdYarnGuid: '',
      WarpPrdYarnGuid: '',
      MinTotalWeightWhole: '',
      MinTotalWeightDecimal: '',
      MaxTotalWeightWhole: '',
      MaxTotalWeightDecimal: '',
      PrdWeaveTypeGuid: '',
      WeaveCode: '',
      PrdCompositionGuid: '',
      MinWarpDensityWhole: '',
      MinWarpDensityDecimal: '',
      MaxWarpDensityWhole: '',
      MaxWarpDensityDecimal: '',
      MinWeftDensityWhole: '',
      MinWeftDensityDecimal: '',
      MaxWeftDensityWhole: '',
      MaxWeftDensityDecimal: '',
      ReedWidth: '',
      MinTotalThreads: '',
      MaxTotalThreads: '',
      Description: '',
      ReedNo: '',
      Dgts: '',
      FabricWidth: '',
      FabricWeightWhole: '',
      FabricWeightDecimal: ''
    };

    setFilters(resetFilters);
    triggerFilter(resetFilters);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  function scrollToTableContainer() {
    const tableContainer = document.querySelector('.weave-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const totalRows = weaveData?.TotalCount ?? tableData.length;
  const page = query?.PageNumber || 1;
  const rowsPerPage = query?.PageSize || 10;
  const totalPages = Math.max(1, weaveData?.TotalPages || Math.ceil(totalRows / rowsPerPage));

  const maxVisiblePages = 7;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const paginationItems = [];

  if (startPage > 1) {
    paginationItems.push(
      <CPaginationItem key="first" onClick={() => handleChangePage(1)}>
        1
      </CPaginationItem>
    );
    if (startPage > 2) {
      paginationItems.push(
        <CPaginationItem key="start-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <CPaginationItem
        key={i}
        active={i === page}
        onClick={() => handleChangePage(i)}
      >
        {i}
      </CPaginationItem>
    );
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationItems.push(
        <CPaginationItem key="end-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
    paginationItems.push(
      <CPaginationItem key="last" onClick={() => handleChangePage(totalPages)}>
        {totalPages}
      </CPaginationItem>
    );
  }

  const currentOrderByColumn = Object.entries({
    weavecode: 'WeaveCode',
    prdweavetypeguid: 'WeaveTypeName',
    totalwarpweight: 'TotalWarpWeight',
    totalweftweight: 'TotalWeftWeight',
    totalweight: 'TotalWeight'
  }).find(([backendField]) => backendField === query?.OrderBy)?.[1] || '';

  const currentOrder = query?.IsDescending === false ? 'asc' : 'desc';
  const hasDecimalFilter = (wholeKey, decimalKey) =>
    String(filters[wholeKey] || '').trim() || String(filters[decimalKey] || '').trim();
  const activeFilterCount = Object.entries(filters)
    .filter(([key, value]) => ![
      'FabricWeightWhole', 'FabricWeightDecimal',
      'MinTotalWeightWhole', 'MinTotalWeightDecimal',
      'MaxTotalWeightWhole', 'MaxTotalWeightDecimal',
      'MinWarpDensityWhole', 'MinWarpDensityDecimal',
      'MaxWarpDensityWhole', 'MaxWarpDensityDecimal',
      'MinWeftDensityWhole', 'MinWeftDensityDecimal',
      'MaxWeftDensityWhole', 'MaxWeftDensityDecimal'
    ].includes(key) && String(value || '').trim() !== '').length
    + (hasDecimalFilter('FabricWeightWhole', 'FabricWeightDecimal') ? 1 : 0)
    + (hasDecimalFilter('MinTotalWeightWhole', 'MinTotalWeightDecimal') ? 1 : 0)
    + (hasDecimalFilter('MaxTotalWeightWhole', 'MaxTotalWeightDecimal') ? 1 : 0)
    + (hasDecimalFilter('MinWarpDensityWhole', 'MinWarpDensityDecimal') ? 1 : 0)
    + (hasDecimalFilter('MaxWarpDensityWhole', 'MaxWarpDensityDecimal') ? 1 : 0)
    + (hasDecimalFilter('MinWeftDensityWhole', 'MinWeftDensityDecimal') ? 1 : 0)
    + (hasDecimalFilter('MaxWeftDensityWhole', 'MaxWeftDensityDecimal') ? 1 : 0);

  const exportColumns = useMemo(() => ([
    { id: 'WeaveTypeName', label: t('weaveType') },
    { id: 'WarpThreadNames', label: `${t('warp')} ${t('yarnName')}` },
    { id: 'WeftThreadNames', label: `${t('weft')} ${t('yarnName')}` },
    { id: 'WarpDensity', label: t('warpDensity') },
    { id: 'WeftDensity', label: t('weftDensity') },
    { id: 'TotalThreads', label: t('totalThreads') },
    { id: 'ReedNo', label: t('reedNo') },
    { id: 'Dgts', label: t('dgts') },
    { id: 'TotalWarpWeight', label: t('totalWarpWeight') },
    { id: 'TotalWeftWeight', label: t('totalWeftWeight') },
    { id: 'TotalWeight', label: `${t('totalWarpWeight')} + ${t('totalWeftWeight')}` },
    { id: 'ReedWidth', label: t('reedWidth') },
  ]), [t]);

  const buildCellValue = (colId, item) => {
    if (colId === 'TotalWarpWeight' || colId === 'TotalWeftWeight' || colId === 'TotalWeight') {
      const totals = getGroupedTotalWeights(item?.WeaveColors);
      if (colId === 'TotalWarpWeight') return formatTotalGram(totals.totalWarpRaw);
      if (colId === 'TotalWeftWeight') return formatTotalGram(totals.totalWeftRaw);
      return formatTotalGram(totals.totalWeightRaw);
    }
    // Technical flat fields
    if (['WarpDensity','WeftDensity'].includes(colId)) {
      return formatDensity(item[colId]);
    }
    if (['ReedWidth','ReedNo','Dgts'].includes(colId)) {
      const v = item[colId];
      return v === undefined || v === null ? '' : String(v);
    }
    // Threads summaries
    if (['WarpThreadCodes','WarpThreadNames','WeftThreadCodes','WeftThreadNames','WarpThreadsSummary','WeftThreadsSummary'].includes(colId)) {
      const colors = Array.isArray(item.WeaveColors) ? item.WeaveColors : [];
      const isWeft = colId.startsWith('Weft');
      const list = colors.filter(c => Number(c?.ThreadType) === (isWeft ? 1 : 2));
      const getUniqueValues = (values) => {
        const seen = new Set();
        return values.filter((value) => {
          const text = String(value || '').trim();
          if (!text) return false;
          const key = text.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      };

      if (colId.endsWith('Codes')) {
        const codes = getUniqueValues(list.map(c => c?.YarnCode));
        return codes.length ? codes.join(' + ') : '';
      }
      if (colId.endsWith('Names')) {
        const names = getUniqueValues(list.map((c) => {
          const code = String(c?.YarnCode || '').trim();
          const name = String(c?.YarnName || '').trim();
          if (code && name) return `${code} - ${name}`;
          return code || name;
        }));
        return names.length ? names.join(' + ') : '';
      }
      // Summary: join all codes (fallback to names/id) with '+'
      const items = getUniqueValues(list.map(c => c?.YarnCode || c?.YarnName || String(c?.WeaveColorId || '')));
      return items.length ? items.join(' + ') : '';
    }
    return item[colId] ?? '';
  };

  const yarnOptions = useMemo(() => {
    if (!dropdownData?.Yarns || !Array.isArray(dropdownData.Yarns)) return [];
    return dropdownData.Yarns.map((item) => ({
      value: String(item.PrdYarnGuid ?? ''),
      label: item.YarnCode ? `${item.YarnCode} - ${item.YarnName || ''}` : (item.YarnName || '')
    })).filter((item) => item.value);
  }, [dropdownData]);

  const weaveTypeOptions = useMemo(() => {
    if (!dropdownData?.WeaveTypes || !Array.isArray(dropdownData.WeaveTypes)) return [];
    return dropdownData.WeaveTypes.map((item) => ({
      value: String(item.PrdWeaveTypeGuid ?? ''),
      label: item.WeaveTypeName || ''
    })).filter((item) => item.value);
  }, [dropdownData]);

  const compositionOptions = useMemo(() => {
    if (!dropdownData?.Compositions || !Array.isArray(dropdownData.Compositions)) return [];
    return dropdownData.Compositions.map((item) => ({
      value: String(item.PrdCompositionGuid ?? ''),
      label: item.ThreadName || ''
    })).filter((item) => item.value);
  }, [dropdownData]);

  return (
    <div className="weave-table-container">
      <CCard className="mb-4">
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? "primary" : "outline-primary"}
                  variant={showFilters ? "solid" : "outline"}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                </span>
                <ExportData
                  columns={exportColumns}
                  rows={tableData}
                  allRows={tableData}
                  buildCellValue={buildCellValue}
                  filenamePrefix="weaves_export"
                  triggerDisabled={!tableData || tableData.length === 0}
                />
              </div>
            </div>
          </div>

          {/* Filtre Alanları */}
          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3">
                {filterFields.map((field) => (
                  <CCol key={field.id} xs={12} sm={6} md={4} lg={3}>
                    <div className="filter-field">
                      <label className={`filter-label ${(field.id.startsWith('Min') || field.id.startsWith('Max')) ? 'filter-label--compact' : ''}`}>
                        {field.label}
                      </label>
                      {field.id === 'FabricWeight' ? (
                        <div className="decimal-input-group">
                          <CFormInput
                            type="text"
                            name="FilterFabricWeightWhole"
                            className="filter-input whole-input"
                            value={filters.FabricWeightWhole}
                            onChange={(e) => handleWholeInput(e, 'FabricWeightWhole')}
                            onKeyDown={(e) => handlePriceKeyDown(e, 'FilterFabricWeightDecimal')}
                            size="sm"
                            placeholder="0"
                          />
                          <span className="decimal-separator">,</span>
                          <CFormInput
                            type="text"
                            name="FilterFabricWeightDecimal"
                            className="filter-input decimal-input"
                            value={filters.FabricWeightDecimal}
                            onChange={(e) => handleDecimalInput(e, 'FabricWeightDecimal')}
                            size="sm"
                            placeholder="00"
                          />
                        </div>
                      ) : ['MinWarpDensity', 'MaxWarpDensity', 'MinWeftDensity', 'MaxWeftDensity'].includes(field.id) ? (
                        <div className="decimal-input-group">
                          <CFormInput
                            type="text"
                            name={`Filter${field.id}Whole`}
                            className="filter-input whole-input"
                            value={filters[`${field.id}Whole`]}
                            onChange={(e) => handleWholeInput(e, `${field.id}Whole`)}
                            onKeyDown={(e) => handlePriceKeyDown(e, `Filter${field.id}Decimal`)}
                            size="sm"
                            placeholder="0"
                          />
                          <span className="decimal-separator">,</span>
                          <CFormInput
                            type="text"
                            name={`Filter${field.id}Decimal`}
                            className="filter-input decimal-input"
                            value={filters[`${field.id}Decimal`]}
                            onChange={(e) => handleDecimalInput(e, `${field.id}Decimal`)}
                            size="sm"
                            placeholder="00"
                          />
                        </div>
                      ) : field.id === 'MinTotalWeight' ? (
                        <div className="decimal-input-group">
                          <CFormInput
                            type="text"
                            name="FilterMinTotalWeightWhole"
                            className="filter-input whole-input"
                            value={filters.MinTotalWeightWhole}
                            onChange={(e) => handleWholeInput(e, 'MinTotalWeightWhole')}
                            onKeyDown={(e) => handlePriceKeyDown(e, 'FilterMinTotalWeightDecimal')}
                            size="sm"
                            placeholder="0"
                          />
                          <span className="decimal-separator">,</span>
                          <CFormInput
                            type="text"
                            name="FilterMinTotalWeightDecimal"
                            className="filter-input decimal-input"
                            value={filters.MinTotalWeightDecimal}
                            onChange={(e) => handleDecimalInput(e, 'MinTotalWeightDecimal')}
                            size="sm"
                            placeholder="00"
                          />
                        </div>
                      ) : field.id === 'MaxTotalWeight' ? (
                        <div className="decimal-input-group">
                          <CFormInput
                            type="text"
                            name="FilterMaxTotalWeightWhole"
                            className="filter-input whole-input"
                            value={filters.MaxTotalWeightWhole}
                            onChange={(e) => handleWholeInput(e, 'MaxTotalWeightWhole')}
                            onKeyDown={(e) => handlePriceKeyDown(e, 'FilterMaxTotalWeightDecimal')}
                            size="sm"
                            placeholder="0"
                          />
                          <span className="decimal-separator">,</span>
                          <CFormInput
                            type="text"
                            name="FilterMaxTotalWeightDecimal"
                            className="filter-input decimal-input"
                            value={filters.MaxTotalWeightDecimal}
                            onChange={(e) => handleDecimalInput(e, 'MaxTotalWeightDecimal')}
                            size="sm"
                            placeholder="00"
                          />
                        </div>
                      ) : ['YarnGuid', 'WeftPrdYarnGuid', 'WarpPrdYarnGuid'].includes(field.id) ? (
                        <SearchableSelect
                          id={`Filter${field.id}`}
                          name={`Filter${field.id}`}
                          options={yarnOptions}
                          value={filters[field.id]}
                          onChange={(selectedValue) => handleFilterChange(field.id, selectedValue)}
                          placeholder={t('all')}
                          displayKey="label"
                          valueKey="value"
                          emptyText={t('No options found')}
                          className="filter-searchable-select"
                          size="sm"
                        />
                      ) : field.id === 'PrdWeaveTypeGuid' ? (
                        <CFormSelect
                          size="sm"
                          value={filters.PrdWeaveTypeGuid}
                          onChange={(e) => handleFilterChange('PrdWeaveTypeGuid', e.target.value)}
                          className="filter-select"
                        >
                          <option value="">{t('all')}</option>
                          {weaveTypeOptions.map((opt, i) => (
                            <option key={opt.value || `wt-${i}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </CFormSelect>
                      ) : field.id === 'PrdCompositionGuid' ? (
                        <CFormSelect
                          size="sm"
                          value={filters.PrdCompositionGuid}
                          onChange={(e) => handleFilterChange('PrdCompositionGuid', e.target.value)}
                          className="filter-select"
                        >
                          <option value="">{t('all')}</option>
                          {compositionOptions.map((opt, i) => (
                            <option key={opt.value || `comp-${i}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                        <div className="filter-input-wrapper">
                          <CIcon icon={cilSearch} className="filter-input-icon" />
                          <CFormInput
                            type="text"
                            placeholder={`${t('search')} ${String(field.label).toLowerCase()}...`}
                            value={filters[field.id]}
                            onChange={(e) => handleFilterChange(field.id, e.target.value)}
                            className="filter-input"
                            size="sm"
                          />
                          {filters[field.id] && (
                            <CButton
                              color="link"
                              size="sm"
                              className="filter-clear-btn"
                              onClick={() => clearSingleFilter(field.id)}
                            >
                              <CIcon icon={cilX} size="sm" />
                            </CButton>
                          )}
                        </div>
                      )}
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell
                      key={column.id}
                      className={`table-header-cell ${column.sortable ? 'sortable-header' : ''}`}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{
                        cursor: column.sortable ? 'pointer' : 'default',
                        paddingRight: '8px',
                        ...(column.id === 'ThreadName' ? { minWidth: '180px', width: '180px' } : {}),
                        ...(headerProps.style || {})
                      }}
                    >
                      {column.label}
                      {column.sortable && currentOrderByColumn === column.id && (
                        <CIcon
                          icon={currentOrder === 'asc' ? cilArrowTop : cilArrowBottom}
                          size="sm"
                          className="ms-1"
                        />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {tableData.length > 0 ? (
                tableData.map((item, index) => {
                  const isExpanded = expandedRows.has(item.WeaveId);
                  const { totalWarpRaw, totalWeftRaw, totalWeightRaw } = getGroupedTotalWeights(item?.WeaveColors);
                  const totalWarpFormatted = formatTotalGram(totalWarpRaw) || '-';
                  const totalWeftFormatted = formatTotalGram(totalWeftRaw) || '-';
                  const totalWeightFormatted = formatTotalGram(totalWeightRaw) || '-';
                  return (
                    <React.Fragment key={item.WeaveId || index}>
                      <CTableRow className={isExpanded ? 'expanded-row' : ''}>
                        <CTableDataCell className="expand-cell">
                          <div
                            className="expand-button"
                            onClick={() => toggleRowExpansion(item.WeaveId)}
                          >
                            <CIcon
                              icon={isExpanded ? cilChevronDoubleDown : cilChevronRight}
                              size="sm"
                              className="expand-icon"
                            />
                          </div>
                        </CTableDataCell>
                        <CTableDataCell>
                          {(page - 1) * rowsPerPage + index + 1}
                        </CTableDataCell>
                        <CTableDataCell>
                          <CBadge color="primary" shape="rounded-pill">
                            {item.WeaveCode}
                          </CBadge>
                        </CTableDataCell>
                        <CTableDataCell>
                          {item.WeaveTypeName || '-'}
                        </CTableDataCell>
                        <CTableDataCell>{totalWarpFormatted}</CTableDataCell>
                        <CTableDataCell>{totalWeftFormatted}</CTableDataCell>
                        <CTableDataCell>{totalWeightFormatted}</CTableDataCell>
                        <CTableDataCell>
                          <div className="d-flex gap-2">
                            <CTooltip content={t('copyWeave')}>
                              <div
                                className="cursor-pointer p-1"
                                onClick={() => handleCopyClick(item)}
                                style={{
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <CIcon
                                  icon={cilRecycle}
                                  size="sm"
                                  style={{ color: '#198754' }}
                                />
                              </div>
                            </CTooltip>

                            <CTooltip content={t('updateWeaveStatus')}>
                              <div
                                className="cursor-pointer p-1"
                                onClick={() => handleEdit(item)}
                                style={{
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <CIcon
                                  icon={cilPencil}
                                  size="sm"
                                  style={{ color: mainColors.primary }}
                                />
                              </div>
                            </CTooltip>

                            <CTooltip content={t('deleteWeaveStatus')}>
                              <div
                                className="cursor-pointer p-1"
                                onClick={() => handleDeleteClick(item)}
                                style={{
                                  borderRadius: '4px',
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <CIcon
                                  icon={cilTrash}
                                  size="sm"
                                  style={{ color: '#dc3545' }}
                                />
                              </div>
                            </CTooltip>
                          </div>
                        </CTableDataCell>
                      </CTableRow>

                      {/* Detail Row */}
                      {isExpanded && (
                        <CTableRow className="detail-row">
                          <CTableDataCell colSpan={columns.length} className="detail-cell">
                            <div className="detail-content">
                              <div className="detail-grid detail-grid--preview">
                                <div className="detail-item">
                                  <span className="detail-label">{t('warpDensity')}:</span>
                                  <span className="detail-value">{formatDensity(item.WarpDensity) || '-'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">{t('weftDensity')}:</span>
                                  <span className="detail-value">{formatDensity(item.WeftDensity) || '-'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">{t('reedWidth')}:</span>
                                  <span className="detail-value">{item.ReedWidth || '-'}</span>
                                </div>
                                <div
                                  className="detail-item detail-item--action"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleOpenCaglikPreview(item)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleOpenCaglikPreview(item);
                                    }
                                  }}
                                >
                                  <span className="detail-label">{t('caglikReport') || 'Çağlık Raporu'}:</span>
                                  <span className="detail-value detail-value--action">
                                    {t('preview') || 'Önizleme Aç'}
                                  </span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">{t('reedNo')}:</span>
                                  <span className="detail-value">{item.ReedNo || '-'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">{t('totalThreads')}:</span>
                                  <span className="detail-value">{item.TotalThreads || '-'}</span>
                                </div>
                              </div>
                            </div>
                          </CTableDataCell>
                        </CTableRow>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center">
                    <div className="no-data-message">
                      {activeFilterCount > 0 ? (
                        <div className="no-results-message">
                          <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                          <div>{t('noResultsFound')}</div>
                          <CButton
                            color="link"
                            size="sm"
                            onClick={clearFilters}
                            className="mt-2"
                          >
                            {t('clearFiltersToSeeAll')}
                          </CButton>
                        </div>
                      ) : (
                        <div className="text-muted">{t('noDataFound')}</div>
                      )}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>

          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select
                  className="form-select form-select-sm"
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>

              <CPagination className="mb-0">
                <CPaginationItem
                  aria-label={t('previous')}
                  onClick={() => page > 1 && handleChangePage(page - 1)}
                  disabled={page === 1}
                >
                  &laquo;
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem
                  aria-label={t('next')}
                  onClick={() => page < totalPages && handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  &raquo;
                </CPaginationItem>
              </CPagination>

              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteWeaveStatus')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteWeaveStatus')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('weaveCode')}:</strong> {deleteModal.item.WeaveCode}<br />
              <strong>{t('description')}:</strong> {deleteModal.item.Description}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('thisActionCannotBeUndone')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>
            {t('cancel')}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>
            {t('delete')}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={copyModal.open} onClose={handleCopyCancel}>
        <CModalHeader>
          <CModalTitle>{t('copyWeave')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {copyModal.item && (
            <div className="bg-light p-3 rounded mb-3">
              <strong>{t('weaveCode')}:</strong> {copyModal.item.WeaveCode}<br />
              <strong>{t('description')}:</strong> {copyModal.item.Description || '-'}
            </div>
          )}
          {copyModal.error && (
            <CAlert color="danger" className="py-2">
              {copyModal.error}
            </CAlert>
          )}
          <label className="form-label" htmlFor="copyWeaveCode">
            {t('newWeaveCode')}
          </label>
          <CFormInput
            id="copyWeaveCode"
            value={copyModal.weaveCode}
            onChange={handleCopyCodeChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCopyConfirm();
              }
            }}
            placeholder={t('enterNewWeaveCode')}
            disabled={copyModal.isSaving}
            autoFocus
          />
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleCopyCancel} disabled={copyModal.isSaving}>
            {t('cancel')}
          </CButton>
          <CButton color="success" onClick={handleCopyConfirm} disabled={copyModal.isSaving}>
            {copyModal.isSaving ? t('saving') : t('copyWeave')}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal
        visible={caglikPreviewModal.open}
        onClose={handleCloseCaglikPreview}
        size="xl"
        alignment="center"
        className="caglik-preview-modal"
      >
        <CModalHeader>
          <CModalTitle>
            {t('caglikReport') || 'Çağlık Raporu'} - {t('preview') || 'Önizleme'}
            {caglikPreviewModal.weaveCode ? ` (${caglikPreviewModal.weaveCode})` : ''}
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="caglik-preview-modal-body">
          {caglikPreviewModal.weaveId ? (
            <CaglikReport
              key={caglikPreviewModal.weaveId}
              initialWeaveId={caglikPreviewModal.weaveId}
              embeddedPreview
            />
          ) : (
            <div className="text-muted">{t('noDataFound')}</div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleCloseCaglikPreview}>
            {t('close') || 'Kapat'}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    weaveList: state.weaveReducer?.weaveList || [],
    weaveData: state.weaveReducer?.weaveData || null,
    weaveHistorySortData: state.weaveReducer?.weaveHistorySortData || {},
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteWeaveRequest: (payload) => {
      dispatch(deleteWeaveRequestAction(payload));
    },
    setAlert: (payload) => {
      dispatch(setAlertAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WeaveTable);
